// Tail-spend demo · domain types
//
// One source of truth for the demo's data shape. The lifecycle state machine
// (lifecycle.ts) is the canonical "what stage is this deal at" — everything
// else here is the persistent record.

import { DealState } from "./lifecycle";

type Brand<K, T> = K & { readonly __brand: T };

export type SupplierId = Brand<string, "SupplierId">;
export type DealId = Brand<string, "DealId">;
export type CategoryId = Brand<string, "CategoryId">;
export type PolicyId = Brand<string, "PolicyId">;
export type ConnectorId = Brand<string, "ConnectorId">;
export type DocumentId = Brand<string, "DocumentId">;
export type ISODateTime = Brand<string, "ISODateTime">;

export const supplierId = (s: string) => s as SupplierId;
export const dealId = (s: string) => s as DealId;
export const categoryId = (s: string) => s as CategoryId;
export const policyId = (s: string) => s as PolicyId;
export const connectorId = (s: string) => s as ConnectorId;
export const isoDateTime = (s: string) => s as ISODateTime;

export type CurrencyCode = "USD" | "EUR" | "GBP";
export interface Money {
    amount: number;
    currency: CurrencyCode;
}
export const usd = (amount: number): Money => ({ amount, currency: "USD" });

export type Percent = number;
export type Confidence = "low" | "medium" | "high";
export type Tone = "green" | "teal" | "amber" | "red" | "blue" | "slate";

// ---------------------------------------------------------------------------
// Categories & policies
// ---------------------------------------------------------------------------
export type CategoryKey =
    | "mro"
    | "packaging"
    | "logistics"
    | "ppe"
    | "rnd_consumables"
    | "office_supplies"
    | "facilities"
    | "lab_services"
    | "it_hardware"
    | "janitorial";

export type LeverKind =
    | "unit_price"
    | "payment_terms"
    | "volume_rebate"
    | "auto_renewal_removal"
    | "fuel_surcharge_cap"
    | "index_passthrough_cap"
    | "accessorials"
    | "service_rate";

export interface Category {
    id: CategoryId;
    key: CategoryKey;
    name: string;
    policyId: PolicyId;
}

export interface WalkAwayFloor {
    unitPriceWorstAcceptablePct?: Percent;
    minPaymentTermsExtensionDays?: number;
    minVolumeRebatePct?: Percent;
    custom?: string[];
}

export interface PolicyVersion {
    version: number;
    /** "Apr 1, 2026" etc. */
    startDate: string;
    /** Omitted on the current version. */
    endDate?: string;
    summary: Array<[label: string, value: string]>;
}

export interface GuardrailPolicy {
    id: PolicyId;
    categoryId: CategoryId;
    active: boolean;
    summary: Array<[label: string, value: string]>;
    /** Ordered oldest → newest. The last entry (no endDate) is the current
     * version and its summary should match `summary` above. */
    versions?: PolicyVersion[];
    autonomousCap: Money;
    buyerESignBand: { min: Money; max: Money };
    handToStrategicAbove: Money;
    walkAwayFloor: WalkAwayFloor;
    leversInPlay: LeverKind[];
    requiredClauses: string[];
    concessionLadderDepth: number;
    minConfidenceForAutoExecute?: Confidence;
}

// ---------------------------------------------------------------------------
// Supplier
// ---------------------------------------------------------------------------
export type SupplierGrade =
    | "A"
    | "A-"
    | "B+"
    | "B"
    | "B-"
    | "C+"
    | "C"
    | "C-"
    | "D";

export type WatchKind =
    | "creeping_auto_renewal"
    | "expiring"
    | "price_drift"
    | "off_contract";

export interface WatchFlag {
    kind: WatchKind;
    detail: string;
}

export interface RenewalTrigger {
    id: string;
    kind: "contract_expiry" | "auto_renewal_window" | "price_drift" | "manual";
    window: string;
    estValue?: Money;
}

export interface Supplier {
    id: SupplierId;
    name: string;
    primaryEmail: string;
    categoryId: CategoryId;
    annualSpend: Money;
    grade?: SupplierGrade;
    hasLiveAgreement: boolean;
    netNewAgreement?: boolean;
    nextRenewal: RenewalTrigger | null;
    lastNegotiatedLabel: string;
    termsSummary: string;
    watch?: WatchFlag;
    currentDealId?: DealId;
}

// ---------------------------------------------------------------------------
// Benchmark
// ---------------------------------------------------------------------------
export interface BenchmarkTick {
    label: string;
    value: string;
    positionPct: number;
}
export interface Benchmark {
    metric: string;
    ticks: BenchmarkTick[];
    supplierPositionPct: number;
    supplierVsMarket: string;
    confidence: Confidence;
    sources: string[];
    refreshedLabel: string;
    rationale: string;
}

// ---------------------------------------------------------------------------
// Negotiation envelope
// ---------------------------------------------------------------------------
export interface Lever {
    kind: LeverKind;
    name: string;
    description: string;
    currentLabel: string;
    targetLabel: string;
    floorLabel: string;
    currentPct: number;
    targetPct: number;
    floorPct: number;
}

// ---------------------------------------------------------------------------
// Conversation thread
// ---------------------------------------------------------------------------
export interface EmailTurn {
    kind: "email";
    side: "agent" | "supplier";
    from: string;
    whenLabel: string;
    subject: string;
    body: string[];
    terms?: Array<[label: string, value: string]>;
    closing?: string;
}

export interface SystemThreadEvent {
    kind: "system";
    variant: "agreement" | "ok" | "warn";
    text: string;
}

export type ThreadItem = EmailTurn | SystemThreadEvent;

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------
export type AuditActor = "agent" | "supplier" | "human";

export interface AuditEvent {
    id: string;
    actor: AuditActor;
    whenLabel: string;
    event: string;
    detail: string;
}

// ---------------------------------------------------------------------------
// Escalation & human decision
// ---------------------------------------------------------------------------
export type EscalationReason =
    | "floor_breach"
    | "above_autonomous_cap"
    | "supplier_silent"
    | "clause_exception"
    | "low_confidence_benchmark"
    | "esign_band"
    | "ladder_exhausted";

export type DecisionOptionKind =
    | "adjust"
    | "trade"
    | "take"
    | "walk"
    | "reassign"
    | "keep";

export interface DecisionOption {
    id: string;
    kind: DecisionOptionKind;
    title: string;
    subtitle: string;
    recommended?: boolean;
}

export interface DecisionPrompt {
    title: string;
    detail: string;
    options: DecisionOption[];
}

export interface DecisionResolution {
    chosenOptionId: string;
    chosenOptionTitle: string;
    decidedByName: string;
    decidedAtLabel: string;
    result: string;
    supplierName: string;
    issue: string;
    resultTone: Tone;
    dealId?: DealId;
}

export interface Escalation {
    reason: EscalationReason;
    severity: "red" | "amber" | "blue";
    title: string;
    description: string;
    decision: DecisionPrompt;
    resolution?: DecisionResolution;
}

// ---------------------------------------------------------------------------
// Autonomy / binding / outcome
// ---------------------------------------------------------------------------
export type AutonomyRouting =
    | "auto_execute"
    | "buyer_esign"
    | "hand_to_strategic"
    | "escalate";

export type BindingMethod =
    | "amendment_under_master"
    | "po_is_contract"
    | "esigned_order_form";

export interface BindingRecord {
    method: BindingMethod;
    title: string;
    description: string;
    files: Array<{ title: string; detail: string }>;
}

export interface OutcomeTermChange {
    lever: string;
    before: string;
    after: string;
    impact: string;
}

export interface Outcome {
    savingsLabel: string;
    savingsSub: string;
    metrics: Array<[label: string, value: string]>;
    termChanges: OutcomeTermChange[];
    binding: BindingRecord;
}

// ---------------------------------------------------------------------------
// Deal
// ---------------------------------------------------------------------------

export interface OverviewNote {
    tone: "green" | "amber" | "red";
    title: string;
    detail: string;
}

export interface Deal {
    id: DealId;
    supplierId: SupplierId;
    supplierName: string;
    supplierEmail: string;
    categoryId: CategoryId;
    categoryName: string;
    annualValue: Money;
    grade?: SupplierGrade;
    progressPct: number;
    stageLabelOverride?: string; // optional human label preferred over derived
    statValuePairs: Array<[label: string, value: string]>;
    opportunityRange?: { low: Money; high: Money };
    envelope: Lever[];
    benchmark?: Benchmark;
    thread: ThreadItem[];
    auditTrail: AuditEvent[];
    overviewNote: OverviewNote;
    /** Lifecycle state — canonical. Drives badge, stage label, and routing. */
    state: DealState;
    escalation?: Escalation;
    outcome?: Outcome;
    /** Pre-rendered short result for list views ("−$4,287 / yr" or "Awaiting you"). */
    resultLabel: string;
    /** Pre-rendered status chip for list views. */
    statusChip: { label: string; tone: Tone };
    /** Manually paused by the user — agent stops auto-acting on this deal. */
    manualPause?: boolean;
}

// ---------------------------------------------------------------------------
// Connectors & notifications
// ---------------------------------------------------------------------------
export interface Connector {
    id: ConnectorId;
    system: string;
    logoLabel: string;
    logoColor: string;
    name: string;
    detail: string;
    status: "active" | "error" | "disconnected";
    account?: string;
    lastSyncedLabel: string;
    eventsToday: number;
    avgLatencyMs: number;
}

export type ConnectorEventDirection = "in" | "out";

export interface ConnectorEvent {
    id: string;
    connectorId: ConnectorId;
    direction: ConnectorEventDirection;
    whenLabel: string;
    title: string;
    detail?: string;
    dealId?: DealId;
}

export interface Notification {
    id: string;
    severity: "info" | "success" | "warn" | "critical";
    title: string;
    subtitle: string;
    whenLabel: string;
    dealId?: DealId;
    read: boolean;
}

// ---------------------------------------------------------------------------
// Reporting
// ---------------------------------------------------------------------------
export interface ReportKpi {
    label: string;
    value: string;
    direction: "up" | "down" | "flat";
    detail: string;
}

export interface CategoryTrend {
    name: string;
    color: string;
    /** Aligned with PortfolioReport.months. */
    values: number[];
}

export type KpiFormat =
    | "currency-k"
    | "currency-m"
    | "currency-auto"
    | "days"
    | "percent"
    | "rating";

export interface KpiTrend {
    label: string;
    color: string;
    format: KpiFormat;
    /** Aligned with PortfolioReport.months. */
    values: number[];
    detail?: string;
}

export interface PortfolioReport {
    kpis: ReportKpi[];
    kpiTrends: KpiTrend[];
    months: string[];
    /** Stacked area chart of $ value captured per month, broken down by
     * procurement category. Sum of values per month = monthly total. */
    valueByCategory: CategoryTrend[];
    /** Stacked area chart of outcome rate per month (sums to 100 each month). */
    outcomeRateByMonth: CategoryTrend[];
}

// ---------------------------------------------------------------------------
// Workspace
// ---------------------------------------------------------------------------
export interface Workspace {
    org: { id: string; name: string; unit: string };
    currentUser: { id: string; name: string; role: string };
    suppliersInScope: number;
    categories: Category[];
    policies: GuardrailPolicy[];
    suppliers: Supplier[];
    deals: Deal[];
    connectors: Connector[];
    systemEvents: ConnectorEvent[];
    notifications: Notification[];
    report: PortfolioReport;
    decisionHistory: DecisionResolution[];
}
